// Swaps the browser tab favicon to the currently-playing level's icon.png,
// restoring the app's default favicon set back in the menu (main.ts calls
// setLevelIcon on world start, resetIcon on quit/win).

const ICON_SELECTOR = 'link[rel~="icon"], link[rel="apple-touch-icon"]';
const iconLinks = Array.from(document.querySelectorAll<HTMLLinkElement>(ICON_SELECTOR));
const defaultHrefs = iconLinks.map((el) => el.getAttribute('href')!);

let objectUrl: string | null = null;

function apply(href: string): void {
  for (const el of iconLinks) el.setAttribute('href', href);
}

export function resetIcon(): void {
  if (objectUrl) {
    URL.revokeObjectURL(objectUrl);
    objectUrl = null;
  }
  iconLinks.forEach((el, i) => el.setAttribute('href', defaultHrefs[i]));
}

/** Builds a favicon from a world's icon.png bytes (or resets to default if null/unreadable). */
export async function setLevelIcon(bytes: Uint8Array | null): Promise<void> {
  if (!bytes) {
    resetIcon();
    return;
  }
  try {
    const bitmap = await createImageBitmap(new Blob([bytes as BlobPart], { type: 'image/png' }));
    const { width: w, height: h } = bitmap;
    const canvas = document.createElement('canvas');
    canvas.width = w;
    canvas.height = h;
    const ctx = canvas.getContext('2d', { willReadFrequently: true })!;
    ctx.imageSmoothingEnabled = false;
    ctx.drawImage(bitmap, 0, 0);
    bitmap.close();

    // Knytt Stories colour-key convention: if the icon has no real alpha,
    // treat magenta (255,0,255) as transparent (AssetManager.buildTexture).
    const img = ctx.getImageData(0, 0, w, h);
    const data = img.data;
    let hasAlpha = false;
    for (let i = 3; i < data.length; i += 4) {
      if (data[i] < 255) {
        hasAlpha = true;
        break;
      }
    }
    if (!hasAlpha) {
      for (let i = 0; i < data.length; i += 4) {
        if (data[i] === 255 && data[i + 1] === 0 && data[i + 2] === 255) data[i + 3] = 0;
      }
      ctx.putImageData(img, 0, 0);
    }

    // Upscale with nearest-neighbour to a size that reads crisply as a tab icon.
    const scale = Math.max(1, Math.round(64 / Math.max(w, h)));
    const out = document.createElement('canvas');
    out.width = w * scale;
    out.height = h * scale;
    const octx = out.getContext('2d')!;
    octx.imageSmoothingEnabled = false;
    octx.drawImage(canvas, 0, 0, out.width, out.height);

    const blob = await new Promise<Blob | null>((resolve) => out.toBlob(resolve, 'image/png'));
    if (!blob) {
      resetIcon();
      return;
    }
    if (objectUrl) URL.revokeObjectURL(objectUrl);
    objectUrl = URL.createObjectURL(blob);
    apply(objectUrl);
  } catch {
    resetIcon();
  }
}
